import React, { useRef } from 'react';
import {
  Alert,
  Box,
  CircularProgress,
  Typography,
} from '@mui/material';
import CloudUploadOutlinedIcon from '@mui/icons-material/CloudUploadOutlined';
import DownloadOutlinedIcon from '@mui/icons-material/DownloadOutlined';
import { Button } from '../../../../../components/ui';
import { useFeatureTheme } from '../../../../../theme/hooks/useFeatureTheme';
import { RECORD_COPY } from '../../constants/recordCopy';

export default function ImportRecordsUploadPanel({
  styles,
  selectedFile,
  errorMessage,
  errorCells,
  isUploading,
  isDownloadingSample,
  isDownloadingErrors,
  onFileSelect,
  onDownloadSample,
  onDownloadErrorFile,
  onUpload,
}) {
  const fileInputRef = useRef(null);
  const { semantic, typography } = useFeatureTheme();

  const openFilePicker = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = (event) => {
    onFileSelect?.(event.target.files?.[0] ?? null);
    event.target.value = '';
  };

  const handleDrop = (event) => {
    event.preventDefault();
    event.stopPropagation();
    const file = event.dataTransfer?.files?.[0];
    onFileSelect?.(file ?? null);
  };

  return (
    <Box sx={styles?.importRecordsCard}>
      <Box sx={styles?.importRecordsActionsRow}>
        <Button
          variant="outline"
          icon={<DownloadOutlinedIcon />}
          onClick={onDownloadSample}
          disabled={isDownloadingSample || isUploading}
          sx={{ width: { xs: '100%', sm: 'auto' } }}
        >
          {isDownloadingSample ? 'Downloading…' : RECORD_COPY.import.downloadSample}
        </Button>
      </Box>

      <Box
        role="button"
        tabIndex={0}
        aria-label={RECORD_COPY.import.uploadHint}
        sx={styles?.importRecordsUploadZone}
        onClick={openFilePicker}
        onKeyDown={(event) => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            openFilePicker();
          }
        }}
        onDragOver={(event) => {
          event.preventDefault();
          event.stopPropagation();
        }}
        onDrop={handleDrop}
      >
        <CloudUploadOutlinedIcon sx={{ color: semantic.text.secondary, fontSize: 40 }} />
        <Typography sx={{ fontWeight: 600, color: semantic.text.primary }}>
          {selectedFile?.name ?? RECORD_COPY.import.uploadHint}
        </Typography>
        <Typography sx={{ fontSize: typography.fontSizeSm, color: semantic.text.secondary }}>
          {RECORD_COPY.import.uploadFormats}
        </Typography>
        {selectedFile ? (
          <Button
            variant="ghost"
            size="sm"
            onClick={(event) => {
              event.stopPropagation();
              onFileSelect?.(null);
            }}
          >
            {RECORD_COPY.import.replaceFile}
          </Button>
        ) : null}
      </Box>

      <input
        ref={fileInputRef}
        type="file"
        accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
        hidden
        onChange={handleFileChange}
      />

      {errorMessage ? (
        <Alert severity="error">
          {errorMessage}
          {errorCells ? (
            <Box sx={{ mt: 1.5 }}>
              <Button
                variant="outline"
                size="sm"
                icon={<DownloadOutlinedIcon />}
                onClick={onDownloadErrorFile}
                disabled={isDownloadingErrors}
              >
                {RECORD_COPY.import.downloadErrorFile}
              </Button>
            </Box>
          ) : null}
        </Alert>
      ) : null}

      <Box sx={styles?.importRecordsActionsRow}>
        <Button
          variant="primary"
          onClick={onUpload}
          disabled={!selectedFile || isUploading}
          icon={isUploading ? <CircularProgress size={16} color="inherit" /> : undefined}
        >
          {isUploading ? RECORD_COPY.import.uploading : RECORD_COPY.import.uploadRecords}
        </Button>
      </Box>
    </Box>
  );
}
